import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/subscribers`, { email, source: "newsletter" });
      setState("done");
      toast.success(data.message || "Welcome to the Venture List.");
    } catch (err) {
      setState("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Please enter a valid email address.");
    }
  };

  return (
    <section id="newsletter" data-testid="newsletter-section" className="relative bg-navy grain-overlay overflow-hidden">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-ocean/25 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 text-center">
        <Reveal>
          <p className="text-xs tracking-[0.24em] uppercase font-semibold text-gold">The Venture List</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory">
            Travel planning, made simpler.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-ivory/70 max-w-xl mx-auto leading-relaxed">
            Receive destination inspiration, practical planning notes, and carefully selected travel
            recommendations in a concise monthly email.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          {state === "done" ? (
            <div
              data-testid="newsletter-success"
              className="mt-10 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-gold/10 px-7 py-4"
            >
              <CheckCircle2 className="h-5 w-5 text-gold" />
              <span className="text-ivory text-sm sm:text-base">
                You're on the list. The next edition arrives soon.
              </span>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="newsletter-form"
              className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="newsletter-email-input"
                className="flex-1 rounded-full bg-ivory/10 border border-ivory/25 px-6 py-4 text-sm text-ivory placeholder:text-ivory/40 outline-none focus:border-gold focus:bg-ivory/15 transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={state === "loading"}
                data-testid="newsletter-submit-button"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-ivory disabled:opacity-70"
              >
                {state === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Join the Venture List
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
          <p className="mt-5 text-xs text-ivory/45">
            One concise email a month. No noise, no resale of your address.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
